import { RootState, AsyncThunk, Dispatch, clearLocalStorage } from '@algoan/dashboard/store/store';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { AuthSession, getSession, parseJwtPayload, signIn, signOut } from '@algoan/authentication';
import { releaseEnv, apiUrl, authRoute } from '@algoan/dashboard/env';
import {
  AmplitudeEvent,
  attachUserIdentityToAmplitude,
  logAmplitudeEvent,
  removeUserIdentityFromAmplitude,
} from '@algoan/dashboard/analytics';
import { updateAuthInformation } from '@algoan/dashboard/store/slice';
import { AccessTokenPayload, AuthInformation } from '@algoan/dashboard/model';
import { selectUsername } from '@algoan/dashboard/store/selectors';
import { LogAction, monitoring } from '@algoan/dashboard/utils';

declare global {
  interface Window {
    isRedirectingToKeycloak: boolean | undefined;
  }
}

const SECURITY_TIME_TO_AVOID_ERROR_DUE_TO_REFRESH_DURING_CALL_API = 10;

const isAuthRoute = (url?: string): boolean => url?.includes(authRoute) ?? false;

const clearState = (): AsyncThunk => async (dispatch: Dispatch) => {
  void dispatch(updateAuthInformation());

  removeUserIdentityFromAmplitude();

  // since the state is saved asynchronously in the localStorage, we clear
  // the localStorage before the redirection
  clearLocalStorage();
};

export const logout = (): AsyncThunk => async (dispatch: Dispatch) => {
  if (window.isRedirectingToKeycloak) {
    return;
  }
  window.isRedirectingToKeycloak = true;
  void dispatch(clearState());

  await signOut();
};

export const redirectToLogin = (): AsyncThunk => async (dispatch: Dispatch) => {
  if (window.isRedirectingToKeycloak) {
    return;
  }
  window.isRedirectingToKeycloak = true;

  void dispatch(clearState());

  signIn({ callbackUrl: window.location.href });
};

export const setAuthToken =
  (session: AuthSession): AsyncThunk =>
  async (dispatch: Dispatch) => {
    const accessToken = session.data.accessToken;
    const tokenParsed = parseJwtPayload<AccessTokenPayload>(accessToken);
    const refreshTokenParsed = parseJwtPayload<AccessTokenPayload>(session.data.refreshToken);
    /* eslint-disable @typescript-eslint/naming-convention */
    const authInfo: AuthInformation = {
      access_token: accessToken,
      id_token: session.data.idToken,
      expires_in: tokenParsed.exp - Date.now() / 1000,
      refresh_expires_in: refreshTokenParsed?.exp,
      refresh_token: session.data.refreshToken,
      requestedAt: tokenParsed.auth_time,
    };

    dispatch(updateAuthInformation(authInfo));

    const userIdentity = {
      userId: tokenParsed?.preferred_username,
      organizationIds: tokenParsed?.organizationIds,
      chatflowIds: tokenParsed?.chatflowIds,
      projectIds: tokenParsed?.projectIds,
      env: releaseEnv,
      team: tokenParsed?.team,
    };

    monitoring.setUser({ ...userIdentity, id: userIdentity.userId });
    attachUserIdentityToAmplitude(userIdentity);
  };

export const hasAccessTokenExpired = (authInformation: AuthInformation): boolean => {
  const elapsedTime = Date.now() - authInformation.requestedAt;

  return (
    elapsedTime > (authInformation.expires_in - SECURITY_TIME_TO_AVOID_ERROR_DUE_TO_REFRESH_DURING_CALL_API) * 1000
  );
};

export const hasRefreshTokenExpired = (authInformation: AuthInformation): boolean => {
  const elapsedTime = Date.now() - authInformation.requestedAt;

  return (
    elapsedTime >
    (authInformation.refresh_expires_in - SECURITY_TIME_TO_AVOID_ERROR_DUE_TO_REFRESH_DURING_CALL_API) * 1000
  );
};

/* eslint-disable @typescript-eslint/naming-convention */
// properties are comming from the backend, we can't name them correctly

const handleAxiosError =
  (
    error: AxiosError<{ error?: string; code?: string }>,
  ): AsyncThunk<AxiosError<{ error?: string }> & { code?: string; status?: number }> =>
  async (dispatch) => {
    const { response } = error;
    if (!('__CANCEL__' in error)) {
      // eslint-disable-next-line no-console
      console.error(error);
    }

    // disconnect when token expired
    if (response?.status === 401 || (typeof response?.data === 'object' && response.data.error === 'invalid_grant')) {
      logAmplitudeEvent(AmplitudeEvent.LOGIN_AUTO_DISCONNECT, {
        reason: 'timeOut',
      });
      monitoring.logAction(LogAction.LOGIN_REDIRECTION, {
        message: 'Invalid token',
        response,
        pathname: window.location.pathname,
      });
      void dispatch(redirectToLogin());
    }

    return {
      ...error,
      code: response?.data?.code ?? error.code,
      status: response?.status,
    };
  };

export const refreshToken = (): AsyncThunk => async (dispatch) => {
  try {
    const session = await getSession();
    if (!session) {
      throw new Error('No auth session');
    }

    await dispatch(setAuthToken(session));
  } catch (error) {
    logAmplitudeEvent(AmplitudeEvent.LOGIN_AUTO_DISCONNECT, {
      reason: 'multiAccount',
    });

    const errorMessage = (error as Error)?.message;
    monitoring.logAction(LogAction.LOGIN_REDIRECTION, {
      message: 'Error while refreshing token',
      error: errorMessage,
      pathname: window.location.pathname,
    });
    // happen when the session was killed from backend
    await dispatch(redirectToLogin());
  }
};

export const authRequest =
  <T>(
    type: Method,
    address: string,
    body?: AxiosRequestConfig['data'],
    config: AxiosRequestConfig = {},
  ): AsyncThunk<AxiosResponse<T>> =>
  async (dispatch, getState): Promise<AxiosResponse<T>> => {
    try {
      const authInformation = getState().auth.authInformation;

      // Added for debugging purposes. To be removed later
      monitoring.logAction(LogAction.PREPARE_API_REQUEST, {
        message: 'Preparing API request',
        authInformation,
        address,
        config,
      });

      if (!isAuthRoute(address)) {
        if (!authInformation || (hasAccessTokenExpired(authInformation) && hasRefreshTokenExpired(authInformation))) {
          monitoring.logAction(LogAction.LOGIN_REDIRECTION, {
            message: 'No auth information or tokens expired',
            pathname: window.location.pathname,
            authInformation,
          });
          // if no authInfo and not an auth request, logout
          await dispatch(redirectToLogin());
          throw new Error('No auth information');
        }

        if (hasAccessTokenExpired(authInformation)) {
          // Added for debugging purposes. To be removed later
          monitoring.logAction(LogAction.PREPARE_API_REQUEST, {
            message: 'Access token expired, refreshing token',
            authInformation,
            config,
          });
          await dispatch(refreshToken());
        }
      }

      const accessToken = getState().auth.authInformation?.access_token;
      const axiosConfig: AxiosRequestConfig = {
        url: address,
        data: body,
        method: type,
        ...config,
        headers: { ...config.headers, authorization: `Bearer ${accessToken}` },
      };

      return await axios.request<T>(axiosConfig);
    } catch (error) {
      throw await dispatch(handleAxiosError(error as AxiosError<{ error?: string; code?: string }>));
    }
  };

export const checkUserPassword =
  (password: string | undefined): AsyncThunk<{ isValid: boolean }> =>
  async (_dispatch, getState) => {
    try {
      const state = getState() as RootState;
      const username = selectUsername(state);
      if (!username) {
        return { isValid: false };
      }

      const body = {
        client_id: 'dashboard-webapp',
        client_secret: undefined,
        grant_type: 'password',
        username: username,
        password: password,
      };

      const headers = new Headers();
      headers.set('Content-Type', 'application/x-www-form-urlencoded');

      const response = await fetch(apiUrl + authRoute, {
        method: 'POST',
        body: new URLSearchParams(body as unknown as Record<string, string>).toString(),
        headers,
      });

      return { isValid: response.status === 200 };
    } catch (error) {
      return { isValid: false };
    }
  };
